import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown'
import ActiveFilters, { ActiveFilterChip } from '../../../components/novarium/ActiveFilters'
import MoreFiltersToggle from '../../../components/novarium/MoreFiltersToggle'
import PeriodField from '../../../components/novarium/PeriodField'
import { VIRTUAL_PANEL_CLASS, VIRTUAL_SCROLL } from '../../../components/novarium/virtualOptions'
import { UrgencyOptionDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { UrgencyFilterField, UrgencySummaryData } from '../useUrgencySummaryData'

/** Valor de la opción «Todas» / «Todos»: el combo no admite `null` como valor seleccionado. */
const ALL_OPTION_ID = -1

/** Los filtros de «Más filtros», en el orden de la pantalla. */
const MORE_FILTERS: readonly UrgencyFilterField[] = ['financierId', 'priorityId']

/** Los chips de «Filtrando por», en el orden de la fila de filtros. */
const CHIP_ORDER: readonly UrgencyFilterField[] = ['templateId', 'financierId', 'priorityId']

const CHIP_LABEL_ID: Record<UrgencyFilterField, string> = {
  templateId: 'UrgencySummaryChipTemplate',
  financierId: 'UrgencySummaryChipFinancier',
  priorityId: 'UrgencySummaryChipPriority',
}

const MORE_FILTERS_ID = 'urg-more-filters'

interface UrgencyFiltersProps {
  data: UrgencySummaryData
}

/** Un combo con opción «Todas»: `null` en los filtros, `ALL_OPTION_ID` en el combo. */
function withAll(options: UrgencyOptionDTO[] | undefined, allLabel: string): UrgencyOptionDTO[] {
  return [{ id: ALL_OPTION_ID, name: allLabel }, ...(options ?? [])]
}

/**
 * Filtros de las atenciones de urgencia, todos al instante (no hay «Consultar»). A la vista, una sola fila: «Período»
 * (un selector con Hoy, Últimos 7 días… y las fechas solo en «Personalizado»), «Guardia» y «Más filtros» (Financiador y
 * Código de triaje). Debajo, los chips «Filtrando por» con todo lo aplicado y «Quitar todos». «Guardia» no se muestra
 * si el sitio tiene una sola.
 */
export default function UrgencyFilters({ data }: UrgencyFiltersProps) {
  const intl = useIntl()
  const { filters, errors, period, rangeText, options, changeFilter, patchFilters, changeDate, commitDates, changePeriod } = data

  const allFemale = intl.formatMessage({ id: 'UrgencySummaryAllFemale' })
  const allMale = intl.formatMessage({ id: 'UrgencySummaryAllMale' })
  const templateOptions = useMemo(() => withAll(options?.templates, allFemale), [options, allFemale])
  const financierOptions = useMemo(() => withAll(options?.financiers, allMale), [options, allMale])
  const priorityOptions = useMemo(() => withAll(options?.priorities, allMale), [options, allMale])
  const optionsReady = options !== null
  const showTemplate = !optionsReady || options.templates.length > 1

  const dateErrors = useMemo(() => ({
    from: errors.from === undefined ? undefined : intl.formatMessage({ id: 'UrgencySummaryDateRequiredError' }),
    to: errors.to === undefined
      ? undefined
      : intl.formatMessage({ id: errors.to === 'order' ? 'UrgencySummaryDateOrderError' : 'UrgencySummaryDateRequiredError' }),
  }), [errors.from, errors.to, intl])

  const moreActive = MORE_FILTERS.filter((field) => filters[field] !== null).length
  // «Más filtros» se abre solo cuando pasa a haber alguno aplicado (también al volver del detalle); después se puede cerrar
  const [moreOpen, setMoreOpen] = useState(moreActive > 0)
  useEffect(() => {
    if (moreActive > 0) setMoreOpen(true)
  }, [moreActive])

  /** Nombre de cada filtro elegido; las opciones todavía sin cargar se nombran por su número. */
  const chips = useMemo<ActiveFilterChip<UrgencyFilterField>[]>(() => {
    const nameOf = (list: UrgencyOptionDTO[] | undefined, id: number): string =>
      list?.find((option) => option.id === id)?.name ?? intl.formatMessage({ id: 'UrgencySummaryChipUnknown' }, { id })
    const lists: Record<UrgencyFilterField, UrgencyOptionDTO[] | undefined> = {
      templateId: options?.templates,
      financierId: options?.financiers,
      priorityId: options?.priorities,
    }
    return CHIP_ORDER.flatMap((field) => {
      const id = filters[field]
      return id === null
        ? []
        : [{ key: field, label: intl.formatMessage({ id: CHIP_LABEL_ID[field] }, { name: nameOf(lists[field], id) }) }]
    })
  }, [filters, options, intl])

  const onSelect = (field: UrgencyFilterField) => (event: DropdownChangeEvent) =>
    changeFilter(field, event.value === ALL_OPTION_ID ? null : (event.value as number))

  return (
    <section className="nv-section" role="search" aria-label={intl.formatMessage({ id: 'UrgencySummaryFilters' })}>
      <div className="nv-form-row">
        <PeriodField
          idPrefix="urg"
          period={period}
          rangeText={rangeText}
          from={filters.from}
          to={filters.to}
          errors={dateErrors}
          onDateChange={changeDate}
          onDatesCommit={commitDates}
          onPeriodChange={changePeriod}
        />
        {showTemplate && (
          <div className="nv-field nv-w-m">
            <label htmlFor="urg-template">{intl.formatMessage({ id: 'UrgencySummaryTemplate' })}</label>
            <Dropdown
              inputId="urg-template"
              value={filters.templateId ?? ALL_OPTION_ID}
              options={templateOptions}
              optionLabel="name"
              optionValue="id"
              disabled={!optionsReady}
              panelClassName="nv-scope"
              onChange={onSelect('templateId')}
            />
          </div>
        )}
        <MoreFiltersToggle open={moreOpen} active={moreActive} controls={MORE_FILTERS_ID} onToggle={() => setMoreOpen((open) => !open)} />
      </div>

      {moreOpen && (
        <div id={MORE_FILTERS_ID} className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="urg-financier">{intl.formatMessage({ id: 'UrgencySummaryFinancier' })}</label>
            <Dropdown
              inputId="urg-financier"
              value={filters.financierId ?? ALL_OPTION_ID}
              options={financierOptions}
              optionLabel="name"
              optionValue="id"
              filter
              filterBy="name"
              virtualScrollerOptions={VIRTUAL_SCROLL}
              panelClassName={`nv-scope ${VIRTUAL_PANEL_CLASS}`}
              emptyFilterMessage={intl.formatMessage({ id: 'UrgencySummaryNoFinanciers' })}
              disabled={!optionsReady}
              onChange={onSelect('financierId')}
            />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="urg-priority">{intl.formatMessage({ id: 'UrgencySummaryPriority' })}</label>
            <Dropdown
              inputId="urg-priority"
              value={filters.priorityId ?? ALL_OPTION_ID}
              options={priorityOptions}
              optionLabel="name"
              optionValue="id"
              disabled={!optionsReady}
              panelClassName="nv-scope"
              onChange={onSelect('priorityId')}
            />
          </div>
        </div>
      )}

      <ActiveFilters
        chips={chips}
        onRemove={(field) => patchFilters({ [field]: null })}
        onClear={() => patchFilters({ templateId: null, financierId: null, priorityId: null })}
      />
    </section>
  )
}
